// AI Agent Studio — "From KB" tab.
//
// Upload one or more KB documents (PDF / Markdown / HTML / TXT, or paste KB
// content inline). The agent-template Lambda (mode: "from-kb") reads the KB
// as Bedrock document content blocks, identifies topics, and emits an
// importable WxCC AI Agent JSON whose Goals / Instructions are derived from
// the KB. Two custom tools are auto-emitted: `lookup_knowledge` (engineer
// wires up post-import to a real KB query endpoint) and `escalate_to_human`.
//
// Shape mirrors AgentTemplateCreator (form tab) — same submit/poll pattern,
// same result panel, same download flow.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Container,
  Title,
  Text,
  Stack,
  Group,
  Card,
  Badge,
  Button,
  Loader,
  Alert,
  Select,
  TextInput,
  Textarea,
  FileInput,
  Table,
  ActionIcon,
  Tooltip,
  Code,
  Divider,
  ScrollArea
} from '@mantine/core';
import {
  IconBook2,
  IconAlertCircle,
  IconDownload,
  IconUpload,
  IconTrash,
  IconCheck,
  IconCopy
} from '@tabler/icons-react';
import PlatformBackButton from '../../components/PlatformBackButton';
import { useAuth } from '../../shared/contexts/AuthContext';
import {
  submitKbAgentJob,
  pollAgentJob,
  isAgentStudioBackendConfigured
} from './api_scripts/agentStudioClient';
import { callQualityJudge } from '../../shared/services/qualityJudgeService';
import JudgeProgressModal from '../../components/JudgeProgressModal';
import { HeaderBadge, RegenerateButton, FindingsAlert } from './components/AgentQualityBadge';

const PLATFORM_OPTIONS = [{ value: 'wxcc', label: 'WxCC (Webex Contact Center)' }];
const AGENT_TYPE_OPTIONS = [
  { value: 'autonomous', label: 'Autonomous (LLM-driven)' },
  { value: 'scripted', label: 'Scripted (intents + entities)' }
];
const CHANNEL_OPTIONS = [
  { value: 'voice', label: 'Voice (VAV2)' },
  { value: 'digital', label: 'Digital (chat / SMS / email)' }
];
const PERSONA_OPTIONS = [
  { value: '', label: '(default — neutral professional)' },
  { value: 'professional', label: 'Professional' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'concise', label: 'Concise' },
  { value: 'clinical', label: 'Clinical / healthcare-appropriate' },
  { value: 'empathetic', label: 'Empathetic' }
];

const MAX_ARTIFACTS = 5;
const ACCEPTED_TYPES = '.pdf,.md,.markdown,.html,.htm,.txt';
const PDF_SOFT_WARN_BYTES = 28 * 1024 * 1024;
const TEXT_SOFT_WARN_CHARS = 180_000;

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const r = String(reader.result || '');
      const comma = r.indexOf(',');
      resolve(comma >= 0 ? r.slice(comma + 1) : r);
    };
    reader.readAsDataURL(file);
  });
}
function fileToText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => resolve(String(reader.result || ''));
    reader.readAsText(file);
  });
}

function downloadJson(filename, obj) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function newRow() {
  return { id: `kb${Math.random().toString(36).slice(2, 8)}`, label: '', file: null };
}

function KbAgentBuilder({ onBackToPlatformSelector }) {
  const [platform, setPlatform] = useState('wxcc');
  const [agentType, setAgentType] = useState('autonomous');
  const [channel, setChannel] = useState('voice');
  const [name, setName] = useState('');
  const [persona, setPersona] = useState('');
  const [notes, setNotes] = useState('');
  const [postImportNotes, setPostImportNotes] = useState('');

  const [rows, setRows] = useState([newRow()]);
  const [inlineText, setInlineText] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [jobStartedAt, setJobStartedAt] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [copiedJson, setCopiedJson] = useState(false);
  const resultsRef = useRef(null);
  const pollAbortRef = useRef(null);

  // Quality-judge advisory state. KB-mode rubric strips kbArtifacts from the
  // formInput sent to the judge — the judge can't ground a "did the agent
  // reflect KB content?" check against the KB without re-reading it (the SPA
  // doesn't keep raw KB bodies in memory after submit, and re-sending PDFs
  // would double the per-import cost). Punted to v2; the form-input fields we
  // do pass let the judge grade standards coverage + persona consistency +
  // constraints + webhook discipline, which is most of the rubric.
  const [judgeEvaluating, setJudgeEvaluating] = useState(false);
  const [qualityScore, setQualityScore] = useState(null);
  const [qualityFindings, setQualityFindings] = useState([]);
  const judgeRunIdRef = useRef(0);
  const lastFormPayloadRef = useRef(null);

  const { getApiToken } = useAuth();
  const backendConfigured = isAgentStudioBackendConfigured();

  const filled = useMemo(() => rows.filter((r) => r.file), [rows]);
  const inlineNonEmpty = inlineText.trim().length > 0;
  const canSubmit = !submitting && name.trim() && (filled.length > 0 || inlineNonEmpty);

  // Poll loop (mirrors AgentTemplateCreator — same shape).
  useEffect(() => {
    if (!jobId || !jobStatus || jobStatus === 'SUCCEEDED' || jobStatus === 'FAILED') return;
    let cancelled = false;
    pollAbortRef.current = { cancel: () => { cancelled = true; } };
    const startedAt = jobStartedAt || Date.now();
    const POLL_MS = 2000;
    const MAX_MS = 5 * 60 * 1000;
    const tick = async () => {
      if (cancelled) return;
      if (Date.now() - startedAt > MAX_MS) {
        if (!cancelled) {
          setError('Job timed out after 5 minutes. Check CloudWatch logs for the Lambda.');
          setSubmitting(false);
          setJobStatus('FAILED');
        }
        return;
      }
      try {
        const row = await pollAgentJob(jobId, getApiToken);
        if (cancelled) return;
        setJobStatus(row.status);
        if (row.status === 'SUCCEEDED') {
          setResult(row.result || null);
          setSubmitting(false);
          if (row.result?.json) {
            startJudge({
              agentJson: row.result.json,
              standards: row.result.standardsSnapshot,
              formInput: lastFormPayloadRef.current
            });
          }
          return;
        }
        if (row.status === 'FAILED') {
          const e = row.error || {};
          setError(e.message || `Job failed (code: ${e.code || 'UNKNOWN'})`);
          setSubmitting(false);
          return;
        }
        setTimeout(tick, POLL_MS);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Polling failed.');
        setSubmitting(false);
        setJobStatus('FAILED');
      }
    };
    setTimeout(tick, POLL_MS);
    return () => { cancelled = true; };
  }, [jobId, jobStatus, jobStartedAt, getApiToken]);

  useEffect(() => {
    if (!jobStartedAt || jobStatus === 'SUCCEEDED' || jobStatus === 'FAILED') {
      setElapsed(0);
      return;
    }
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - jobStartedAt) / 1000)), 1000);
    return () => clearInterval(id);
  }, [jobStartedAt, jobStatus]);

  useEffect(() => {
    if (result?.json && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [result]);

  const handleFileChange = (idx, file) => {
    setError(null);
    setRows((prev) => {
      const next = [...prev];
      if (!file) {
        next[idx] = { ...next[idx], file: null };
        return next;
      }
      if ((file.type || '').includes('pdf') && file.size > PDF_SOFT_WARN_BYTES) {
        setError(`${file.name}: ${(file.size / 1024 / 1024).toFixed(1)} MB is near the 32 MB Bedrock PDF cap.`);
      }
      next[idx] = {
        ...next[idx],
        file,
        label: next[idx].label || file.name.replace(/\.[^.]+$/, '').slice(0, 60)
      };
      return next;
    });
  };

  const addRow = () => setRows((prev) => (prev.length >= MAX_ARTIFACTS ? prev : [...prev, newRow()]));
  const removeRow = (idx) =>
    setRows((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));
  const updateRow = (idx, patch) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  // Fires after polling resolves SUCCEEDED. KB-mode rubric grades the same
  // agentstudio-agent kind but with kbArtifacts stripped from formInput (see
  // the explainer comment near the state declarations above).
  const startJudge = async ({ agentJson, standards, formInput }) => {
    if (!agentJson || !standards || !formInput) return;
    const runId = ++judgeRunIdRef.current;
    setJudgeEvaluating(true);
    setQualityScore(null);
    setQualityFindings([]);
    try {
      const data = await callQualityJudge({
        kind: 'agentstudio-agent',
        formInput,
        agentJson,
        standards
      });
      if (judgeRunIdRef.current !== runId) return;
      if (typeof data?.overallScore === 'number') setQualityScore(data.overallScore);
      if (Array.isArray(data?.perItem)) setQualityFindings(data.perItem);
    } catch (e) {
      if (judgeRunIdRef.current !== runId) return;
      // eslint-disable-next-line no-console
      console.warn('quality-judge call failed:', e);
    } finally {
      if (judgeRunIdRef.current === runId) setJudgeEvaluating(false);
    }
  };

  const handleGenerate = async (priorJudgeFindings) => {
    if (!canSubmit) return;
    if (pollAbortRef.current) pollAbortRef.current.cancel();
    setSubmitting(true);
    setError(null);
    setResult(null);
    setJobId(null);
    setJobStatus(null);
    setJobStartedAt(null);
    setQualityScore(null);
    setQualityFindings([]);
    try {
      const kbArtifacts = await Promise.all(
        filled.map(async (r) => {
          const isPdf = (r.file.type || '').includes('pdf');
          return {
            id: r.id,
            label: r.label || r.file.name.replace(/\.[^.]+$/, '').slice(0, 60),
            kind: isPdf ? 'pdf' : 'text',
            body: isPdf ? await fileToBase64(r.file) : await fileToText(r.file),
            mediaType: r.file.type || (isPdf ? 'application/pdf' : 'text/plain')
          };
        })
      );
      const body = {
        platform,
        agentType,
        channel,
        name: name.trim(),
        kbArtifacts,
        ...(inlineNonEmpty ? { kbInlineText: inlineText } : {}),
        ...(persona ? { persona } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        ...(postImportNotes.trim() ? { postImportNotes: postImportNotes.trim() } : {}),
        ...(Array.isArray(priorJudgeFindings) && priorJudgeFindings.length > 0
          ? { priorJudgeFindings }
          : {})
      };
      // Stash the form fields (kbArtifacts excluded) so the judge has something
      // to grade form-input coverage against without re-reading PDFs.
      lastFormPayloadRef.current = {
        platform, agentType, channel, name: body.name,
        mode: 'from-kb',
        kbArtifactCount: kbArtifacts.length,
        kbInlineTextChars: inlineNonEmpty ? inlineText.length : 0,
        ...(persona ? { persona } : {}),
        ...(body.notes ? { notes: body.notes } : {}),
        ...(body.postImportNotes ? { postImportNotes: body.postImportNotes } : {})
      };
      const submitted = await submitKbAgentJob(body, getApiToken);
      setJobId(submitted.jobId);
      setJobStatus(submitted.status || 'PENDING');
      setJobStartedAt(Date.now());
    } catch (e) {
      setError(e?.context?.detail || e.message || 'KB generation failed.');
      setSubmitting(false);
    }
  };

  const handleRegenerate = (findings) => handleGenerate(findings);

  const filenameSafe = useMemo(() => {
    const slug = (name || 'agent-from-kb')
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 64) || 'agent-from-kb';
    return `${slug}.json`;
  }, [name]);

  const handleCopyJson = async () => {
    if (!result?.json) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(result.json, null, 2));
      setCopiedJson(true);
      setTimeout(() => setCopiedJson(false), 2000);
    } catch {
      /* clipboard not allowed; user can still use Download */
    }
  };

  return (
    <Container size="xl" py="lg">
      <Group justify="space-between" mb="md">
        <PlatformBackButton onClick={onBackToPlatformSelector} color="cyan" mb={0} />
      </Group>

      <Stack gap="md">
        <Group gap="sm">
          <IconBook2 size={28} color="var(--mantine-color-cyan-6)" />
          <Title order={2}>Agent Studio — From KB</Title>
          <Badge color="cyan" variant="light">v1 — WxCC</Badge>
        </Group>

        <Text c="dimmed">
          Upload a customer knowledge base (PDF / Markdown / HTML / TXT, or paste KB text inline).
          Bedrock Sonnet 4.6 reads the KB as document content blocks, identifies topics, and emits an
          importable WxCC AI Agent JSON whose <strong>Goals + Instructions</strong> are derived from the KB.
          Two custom tools are auto-wired — <Code>lookup_knowledge</Code> (you replace the placeholder
          webhook URL post-import with your actual KB query endpoint) and <Code>escalate_to_human</Code>.
        </Text>

        {!backendConfigured && (
          <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
            AI Agent Studio API URL is not configured. Set
            <Code> REACT_APP_AGENT_STUDIO_API_BASE_URL </Code> in your environment to enable generation.
          </Alert>
        )}

        <Card withBorder shadow="sm" radius="md" p="lg">
          <Stack gap="md">
            <Group grow>
              <Select
                label="Target platform"
                data={PLATFORM_OPTIONS}
                value={platform}
                onChange={(v) => v && setPlatform(v)}
              />
              <Select
                label="Agent type"
                data={AGENT_TYPE_OPTIONS}
                value={agentType}
                onChange={(v) => v && setAgentType(v)}
                description={
                  agentType === 'autonomous'
                    ? 'LLM-driven (virtualagent). Goals + Instructions + Tools.'
                    : 'Intent + entity classifier (mlbot). Topics map to intents.'
                }
              />
              <Select
                label="Channel"
                data={CHANNEL_OPTIONS}
                value={channel}
                onChange={(v) => v && setChannel(v)}
              />
            </Group>

            <TextInput
              label="Agent name"
              placeholder="e.g. Acme Support Bot"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              required
              maxLength={256}
            />

            <Group grow>
              <Select
                label="Persona / tone"
                data={PERSONA_OPTIONS}
                value={persona}
                onChange={(v) => setPersona(v || '')}
              />
            </Group>
          </Stack>
        </Card>

        <Card withBorder shadow="sm" radius="md" p="lg">
          <Stack gap="sm">
            <Title order={5}>Knowledge base</Title>
            <Text size="sm" c="dimmed">
              Up to {MAX_ARTIFACTS} files. PDFs ride Bedrock&apos;s document content block (32 MB / 100
              pages each). Markdown / HTML / TXT pass inline. Or paste KB content directly in the
              textarea below.
            </Text>

            <Table withTableBorder withColumnBorders verticalSpacing="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: '35%' }}>Label</Table.Th>
                  <Table.Th>File</Table.Th>
                  <Table.Th style={{ width: 60 }}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((r, i) => (
                  <Table.Tr key={r.id}>
                    <Table.Td>
                      <Textarea
                        autosize
                        minRows={1}
                        placeholder={`KB article ${i + 1}`}
                        value={r.label}
                        onChange={(e) => updateRow(i, { label: e.currentTarget.value })}
                      />
                    </Table.Td>
                    <Table.Td>
                      <FileInput
                        placeholder="Choose .pdf / .md / .html / .txt"
                        accept={ACCEPTED_TYPES}
                        value={r.file}
                        leftSection={<IconUpload size={14} />}
                        onChange={(file) => handleFileChange(i, file)}
                        clearable
                      />
                    </Table.Td>
                    <Table.Td>
                      {rows.length > 1 && (
                        <Tooltip label="Remove this row">
                          <ActionIcon variant="subtle" color="red" onClick={() => removeRow(i)}>
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            <Group justify="flex-start">
              <Button variant="light" size="xs" onClick={addRow} disabled={rows.length >= MAX_ARTIFACTS}>
                + Add another artifact
              </Button>
            </Group>

            <Divider label="OR / additionally — paste KB content" labelPosition="left" />

            <Textarea
              autosize
              minRows={3}
              maxRows={12}
              placeholder="Paste KB markdown, FAQ entries, prompt-and-answer pairs, etc."
              value={inlineText}
              onChange={(e) => setInlineText(e.currentTarget.value)}
              description={
                inlineText.length > TEXT_SOFT_WARN_CHARS
                  ? `${inlineText.length.toLocaleString()} chars — near the 200K backend cap.`
                  : null
              }
            />
          </Stack>
        </Card>

        <Card withBorder shadow="sm" radius="md" p="lg">
          <Stack gap="sm">
            <Title order={5}>Notes (optional)</Title>
            <Textarea
              label="Engineer notes — extra framing for the model"
              autosize
              minRows={2}
              placeholder="e.g. Deployment is voice-only; prefer brief responses; customer brand is X (only mentioned in TTS, not in matchers)."
              value={notes}
              onChange={(e) => setNotes(e.currentTarget.value)}
            />
            <Textarea
              label="Post-import wiring notes"
              autosize
              minRows={2}
              description="Will be inserted into the generated agent's description as a POST-IMPORT TODO so you remember what to wire up."
              placeholder="e.g. lookup_knowledge webhook → https://your.middleware/kb/query; escalate_to_human → BlindTransfer to Support queue."
              value={postImportNotes}
              onChange={(e) => setPostImportNotes(e.currentTarget.value)}
            />
          </Stack>
        </Card>

        <Group justify="flex-end">
          <Button
            size="md"
            color="cyan"
            onClick={() => handleGenerate()}
            disabled={!canSubmit}
            leftSection={submitting ? <Loader size={14} color="white" /> : null}
          >
            {submitting
              ? jobStatus === 'PENDING'
                ? `Queued… ${elapsed}s`
                : `Generating… ${elapsed}s`
              : 'Generate agent JSON'}
          </Button>
        </Group>

        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {error}
          </Alert>
        )}

        {result?.json && (
          <Card withBorder shadow="sm" radius="md" p="lg" ref={resultsRef}>
            <Group justify="space-between" mb="md">
              <Group gap="sm">
                <Title order={4}>Generated agent JSON</Title>
                {result.model && (
                  <Badge variant="outline" color="gray">model: {result.model}</Badge>
                )}
                <Badge variant="dot" color="cyan">mode: from-kb</Badge>
                {typeof result.kbArtifactCount === 'number' && (
                  <Badge variant="light" color="violet">{result.kbArtifactCount} KB files</Badge>
                )}
                <HeaderBadge
                  evaluating={judgeEvaluating}
                  qualityScore={qualityScore}
                  color="cyan"
                />
              </Group>
              <Group gap="xs">
                <RegenerateButton
                  qualityFindings={qualityFindings}
                  onRegenerate={handleRegenerate}
                  color="cyan"
                />
                <Button
                  variant="light"
                  leftSection={copiedJson ? <IconCheck size={14} /> : <IconCopy size={14} />}
                  onClick={handleCopyJson}
                >
                  {copiedJson ? 'Copied' : 'Copy JSON'}
                </Button>
                <Button
                  color="cyan"
                  leftSection={<IconDownload size={14} />}
                  onClick={() => downloadJson(filenameSafe, result.json)}
                >
                  Download {filenameSafe}
                </Button>
              </Group>
            </Group>

            {Array.isArray(result.appliedStandards) && result.appliedStandards.length > 0 && (
              <Group gap="xs" mb="md">
                {result.appliedStandards.map((s) => (
                  <Badge key={s} color="cyan" variant="light">{s}</Badge>
                ))}
              </Group>
            )}

            <FindingsAlert qualityFindings={qualityFindings} color="cyan" />

            <ScrollArea h={320}>
              <Code block style={{ whiteSpace: 'pre' }}>
                {JSON.stringify(result.json, null, 2)}
              </Code>
            </ScrollArea>

            <Alert color="cyan" mt="md" icon={<IconBook2 size={16} />}>
              <strong>Post-import TODO:</strong> open the generated agent in WxCC AI Agent Studio
              and replace the placeholder <Code>webhook_url</Code> on <Code>lookup_knowledge</Code>{' '}
              (KB query endpoint) and <Code>escalate_to_human</Code> (transfer flow). The
              llm_agent_description includes a reminder of what to wire.
            </Alert>
          </Card>
        )}
      </Stack>

      <JudgeProgressModal
        opened={judgeEvaluating}
        onClose={() => { /* parent controls via judgeEvaluating flag */ }}
        kind="agentstudio-agent"
        color="cyan"
      />
    </Container>
  );
}

export default KbAgentBuilder;
